import { useQuery } from '@tanstack/react-query'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

// Shape mirrors backend/internal/api/enterprise.go — one call feeds the
// entire /enterprise page.

export type EnterprisePeriodKind = 'day' | 'week' | 'month' | 'year'
export type EnterpriseCompare = 'prior' | 'ly' | 'baseline'

export type EnterprisePeriod = {
  kind: EnterprisePeriodKind
  compare: EnterpriseCompare
  start: string
  end: string
  prior_start: string
  prior_end: string
  title: string
  range_label: string
  prior_label: string
  bucket_label: string
  buckets: string[]
}

export type EnterpriseKPIKind =
  | 'up-good' | 'down-good'
  | 'up-bad'  | 'down-bad'
  | 'flat'

export type EnterpriseKPI = {
  value: number
  prior: number
  delta: number
  delta_abs?: number
  abs_label?: string
  kind: EnterpriseKPIKind
  suffix?: string
  spark: number[]
}

export type EnterprisePatch = {
  patch: string
  jobs: number
  completed: number
  failed: number
  ftf: number
  open: number
  mix: [number, number, number, number] // critical / high / warn / info
  tier: 'ok' | 'warn' | 'bad'
  lat?: number
  lng?: number
}

export type EnterpriseAsset = {
  asset_id: string
  name: string
  type: string
  ref: string
  faults: number
}

export type EnterpriseEngineerRow = {
  engineer_id: string
  name: string
  external_ref: string
  jobs: number
  ftf: number
  delta: number
  dir: 'up' | 'down' | 'flat'
}

export type EnterpriseSummary = {
  period: EnterprisePeriod
  kpis: {
    completed: EnterpriseKPI
    ftf: EnterpriseKPI
    sla: EnterpriseKPI
    repeat: EnterpriseKPI
    criticals: EnterpriseKPI
    raised: EnterpriseKPI
  }
  trends: {
    volume: {
      completed: number[]
      failed: number[]
      cancelled: number[]
    }
    ftf: number[]
    severity: {
      critical: number[]
      high: number[]
      warn: number[]
      info: number[]
    }
  }
  leaderboards: {
    worst_patches: EnterprisePatch[]
    top_assets: EnterpriseAsset[]
    engineers: EnterpriseEngineerRow[]
  }
  patches: EnterprisePatch[]
  narratives: string[]
}

export function useEnterpriseSummary(
  period: EnterprisePeriodKind = 'week',
  compare: EnterpriseCompare = 'prior',
) {
  const { accessToken } = useAuth()
  const client = makeClient(() => accessToken)
  return useQuery({
    queryKey: ['analytics', 'enterprise', period, compare],
    queryFn: () =>
      client.call<EnterpriseSummary>(
        `/api/v1/analytics/enterprise?period=${period}&compare=${compare}`,
      ),
    staleTime: 60_000,
  })
}
